// Sprint 11 — visual previews for AGENT_OPTIONS pills.
//
// Quick-reply options that represent visual values (brand colours, font
// families) are useless as plain text. The agent emits a `previewKind`
// hint when it knows ahead of time, otherwise we auto-detect from the
// option labels. Both signals route to the same per-option renderers in
// `ChatOptionWidget`.

export type PreviewKind = 'colour' | 'font' | 'none';

/**
 * Hex extractor — works on labels like "#1E40AF (Deep Blue)" or "1e40af".
 * Three-char shorthand is only accepted with an explicit `#` prefix
 * because bare 3-digit numbers in prose ("180°", "404 page") would
 * otherwise be misclassified as colours.
 */
const HEX_HASH_RE = /(#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3})\b/;
const HEX_BARE_RE = /\b([0-9a-fA-F]{6})\b/;

export function extractHex(label: string): string | null {
  const hashMatch = label.match(HEX_HASH_RE);
  if (hashMatch) return hashMatch[1].toUpperCase();
  const bareMatch = label.match(HEX_BARE_RE);
  if (bareMatch) {
    // Reject if the 6-char run is actually a fragment of a longer
    // alphanumeric token (URLs, branch names like "feature/us-abcdef-12").
    // We only accept bare hex when it stands alone in the label.
    const candidate = bareMatch[1];
    if (label.trim() === candidate || /^[0-9a-fA-F]{6}$/.test(label.trim())) {
      return `#${candidate.toUpperCase()}`;
    }
  }
  return null;
}

/**
 * Catalogue of font families we know how to preview. The label-detection
 * is case-sensitive on the canonical name as it appears in the option,
 * so pick the form the agent emits ("Plus Jakarta Sans", not
 * "plus_jakarta_sans"). Each entry is a Google Fonts spec used to build
 * the dynamic <link> for the preview render.
 */
export interface FontFamilyPreview {
  /** Display name as the agent prints it. Used for substring matching. */
  name: string;
  /** CSS font-family stack used to render the preview label. */
  cssStack: string;
  /** Google Fonts URL fragment, or null when the font is system-loaded. */
  googleSpec: string | null;
}

export const FONT_FAMILY_CATALOGUE: FontFamilyPreview[] = [
  { name: 'Inter',             cssStack: "'Inter', system-ui, sans-serif",                                 googleSpec: 'Inter:wght@400;600' },
  { name: 'Manrope',           cssStack: "'Manrope', system-ui, sans-serif",                               googleSpec: 'Manrope:wght@400;700' },
  { name: 'Plus Jakarta Sans', cssStack: "'Plus Jakarta Sans', system-ui, sans-serif",                     googleSpec: 'Plus+Jakarta+Sans:wght@400;700' },
  { name: 'IBM Plex Sans',     cssStack: "'IBM Plex Sans', system-ui, sans-serif",                         googleSpec: 'IBM+Plex+Sans:wght@400;600' },
  { name: 'IBM Plex Mono',     cssStack: "'IBM Plex Mono', ui-monospace, monospace",                       googleSpec: 'IBM+Plex+Mono:wght@400;600' },
  { name: 'JetBrains Mono',    cssStack: "'JetBrains Mono', ui-monospace, monospace",                      googleSpec: 'JetBrains+Mono:wght@400;700' },
  { name: 'Roboto',            cssStack: "'Roboto', system-ui, sans-serif",                                googleSpec: 'Roboto:wght@400;700' },
  { name: 'Open Sans',         cssStack: "'Open Sans', system-ui, sans-serif",                             googleSpec: 'Open+Sans:wght@400;700' },
  { name: 'Lato',              cssStack: "'Lato', system-ui, sans-serif",                                  googleSpec: 'Lato:wght@400;700' },
  { name: 'Poppins',           cssStack: "'Poppins', system-ui, sans-serif",                               googleSpec: 'Poppins:wght@400;700' },
  { name: 'Source Sans 3',     cssStack: "'Source Sans 3', system-ui, sans-serif",                         googleSpec: 'Source+Sans+3:wght@400;700' },
  { name: 'Nunito',            cssStack: "'Nunito', system-ui, sans-serif",                                googleSpec: 'Nunito:wght@400;700' },
  { name: 'Work Sans',         cssStack: "'Work Sans', system-ui, sans-serif",                             googleSpec: 'Work+Sans:wght@400;700' },
  { name: 'DM Sans',           cssStack: "'DM Sans', system-ui, sans-serif",                               googleSpec: 'DM+Sans:wght@400;700' },
  { name: 'System UI',         cssStack: 'system-ui, -apple-system, sans-serif',                           googleSpec: null },
];

/**
 * Look up a font preview by label (substring match, longest name first
 * so "IBM Plex Sans" matches before "Plex").
 */
const SORTED_FONT_NAMES = [...FONT_FAMILY_CATALOGUE].sort((a, b) => b.name.length - a.name.length);

export function matchFontFamily(label: string): FontFamilyPreview | null {
  for (const entry of SORTED_FONT_NAMES) {
    if (label.includes(entry.name)) return entry;
  }
  return null;
}

/**
 * Auto-detect the previewKind for a list of option labels. Used when
 * the agent didn't supply an explicit `previewKind`. Heuristic: a
 * STRICT majority of labels must carry a recognisable preview before
 * we treat the whole group as that kind — a 50/50 split (e.g. one
 * hex label and one non-hex) is NOT enough, so the renderer never
 * flips styles halfway and never misclassifies a mixed list.
 * Codex P2 on PR #689 — was previously `>=` against `Math.ceil(n/2)`,
 * which let 1/2 trigger a kind on even-sized lists.
 */
export function detectPreviewKind(labels: string[]): PreviewKind {
  if (labels.length === 0) return 'none';
  const hexHits  = labels.filter(l => extractHex(l) != null).length;
  const fontHits = labels.filter(l => matchFontFamily(l) != null).length;
  const half = labels.length / 2;
  if (hexHits  > half) return 'colour';
  if (fontHits > half) return 'font';
  return 'none';
}

/** Tracks which Google Fonts specs we've already injected into <head>. */
const loadedFontSpecs = new Set<string>();

/**
 * Idempotently inject the Google Fonts <link> for a font preview entry.
 * Safe to call from a render — does nothing on the server (where
 * `document` is undefined) and dedupes so multiple pills sharing the
 * same family don't pile up <link> tags.
 */
export function ensureFontLoaded(entry: FontFamilyPreview): void {
  if (entry.googleSpec == null) return;
  if (typeof document === 'undefined') return;
  if (loadedFontSpecs.has(entry.googleSpec)) return;
  loadedFontSpecs.add(entry.googleSpec);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${entry.googleSpec}&display=swap`;
  link.dataset.blueprintFontPreview = entry.name;
  document.head.appendChild(link);
}

/** Test-only reset of the load cache. */
export function _resetFontLoadCacheForTests(): void {
  loadedFontSpecs.clear();
}
